<template>
  <div>
    <b-card>
      <b-row>
        <b-col cols="12" md="2">
          <label for="dateRange">Date Range</label>
          <b-form-select
            id="dateRange"
            v-model="filters.dateRange"
            :options="dateRanges"
          />
        </b-col>

        <b-col cols="6" md="2">
          <label for="dateFrom">Date From</label>
          <flat-pickr
            v-model="filters.startDate"
            id="dateFrom"
            class="form-control"
            :config="configFrom"
            placeholder="Date From"
          />
        </b-col>

        <b-col cols="6" md="2">
          <label for="dateTo">Date To</label>
          <flat-pickr
            v-model="filters.endDate"
            id="dateTo"
            class="form-control"
            :config="configTo"
            placeholder="Date To"
          />
        </b-col>

        <b-col cols="6" md="2">
          <label for="status">Status</label>
          <b-form-select
            id="status"
            v-model="filters.status"
            :options="statusOptions"
          />
        </b-col>

        <b-col cols="6" md="2">
          <label for="tokenSymbol">Token</label>
          <b-form-input
            id="tokenSymbol"
            v-model="filters.tokenSymbol"
            placeholder="e.g. BTC, XRP"
            class="text-uppercase"
          />
        </b-col>

        <b-col cols="6" md="2">
          <label for="userId">User ID</label>
          <b-form-input
            id="userId"
            v-model="filters.userId"
            placeholder="User ID"
          />
        </b-col>

        <b-col md="12" class="d-flex justify-content-end mt-1">
          <b-button variant="outline-dark" @click="handleClearFilters"
            >Clear</b-button
          >
        </b-col>
      </b-row>
    </b-card>

    <b-row>
      <b-col cols="12">
        <b-card no-body>
          <b-table
            responsive
            :items="items"
            :fields="fields"
            thead-class="text-nowrap"
            :busy="loading"
            show-empty
            hover
            class="mb-0 withdrawal-table"
          >
            <template #cell(requester)="{ item }">
              <div class="d-flex flex-column">
                <strong>{{ item.username || "-" }}</strong>
                <small class="text-muted">
                  {{
                    [item.firstName, item.middleName, item.lastName]
                      .filter(Boolean)
                      .join(" ") || "-"
                  }}
                </small>
              </div>
            </template>

            <template #cell(token)="{ item }">
              <div class="d-flex align-items-center" style="gap: 6px">
                <b-badge variant="light-primary" class="text-uppercase">
                  {{ item.tokenSymbol || "-" }}
                </b-badge>
                <small v-if="item.network" class="text-muted">
                  {{ item.network }}
                </small>
              </div>
            </template>

            <template #cell(amount)="{ item }">
             {{
            item.amount != null
              ? formatCurrencyBuiltInFunc(item.amount)
              : "-"
              }}
            </template>

            <template #cell(address)="{ item }">
              <div
                class="d-flex align-items-center"
                style="gap: 6px; min-width: 160px"
              >
                <span :title="item.address">{{
                  maskAddress(item.address)
                }}</span>
                <feather-icon
                  v-if="item.address"
                  icon="CopyIcon"
                  size="14"
                  class="cursor-pointer text-primary"
                  @click="copyToClipboard(item.address, 'Address copied')"
                />
              </div>
            </template>

            <template #cell(destinationTag)="{ item }">
              {{ item.destinationTag || "-" }}
            </template>

            <template #cell(paymentId)="{ item }">
              <div
                v-if="item.paymentId"
                class="d-flex align-items-center"
                style="gap: 6px"
              >
                <span :title="item.paymentId">
                  {{ truncateMiddle(item.paymentId, 10) }}
                </span>
                <feather-icon
                  icon="CopyIcon"
                  size="14"
                  class="cursor-pointer text-primary"
                  @click="copyToClipboard(item.paymentId, 'Payment ID copied')"
                />
              </div>
              <span v-else>-</span>
            </template>

            <template #cell(createdTime)="{ item }">
              {{
                item.createdTime
                  ? format(new Date(item.createdTime), "dd-MM-yyyy HH:mm")
                  : "-"
              }}
            </template>

            <template #cell(completedTime)="{ item }">
              {{
                item.completedTime
                  ? format(new Date(item.completedTime), "dd-MM-yyyy HH:mm")
                  : "-"
              }}
            </template>

            <template #cell(status)="{ item }">
              <b-badge
                :variant="statusVariant(item.status)"
                class="text-nowrap"
              >
                {{ statusLabel(item.status) }}
              </b-badge>
            </template>

            <template #cell(notes)="{ item }">
              <div style="min-width: 180px">
                <div v-if="item.reviewComment">
                  <small class="text-success">Review: </small>
                  {{ item.reviewComment }}
                </div>
                <div v-if="item.notes">
                  <small class="text-muted">Notes: </small>
                  {{ item.notes }}
                </div>
                <div v-if="item.error" class="text-danger">
                  <small>Error: </small>{{ item.error }}
                </div>
                <span
                  v-if="!item.reviewComment && !item.notes && !item.error"
                  >-</span
                >
              </div>
            </template>

            <template #cell(actions)="{ item }">
              <b-button-group>
                <b-button
                  v-if="canApprove(item.status)"
                  size="sm"
                  variant="success"
                  class="text-nowrap"
                  @click="openApproveModal(item)"
                >
                  Approve
                </b-button>
                <b-button
                  v-if="canReject(item.status)"
                  size="sm"
                  variant="outline-danger"
                  class="text-nowrap"
                  @click="openRejectModal(item)"
                >
                  Reject
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="text-nowrap"
                  @click="openViewModal(item)"
                >
                  View
                </b-button>
              </b-button-group>
            </template>
          </b-table>

          <div
            class="card-footer d-flex flex-wrap justify-content-between align-items-center"
          >
            <div class="d-flex align-items-center">
              <span class="mr-1 text-nowrap">Rows per page</span>
              <b-form-select
                v-model="limit"
                :options="limitOptions"
                size="sm"
                class="mr-1"
              />
              <span class="text-nowrap">{{ paginationSummary }}</span>
            </div>

            <b-pagination
              v-if="pagination.total > 0"
              v-model="page"
              :total-rows="pagination.total"
              :per-page="limit"
              first-number
              last-number
            />
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Approve Modal -->
    <b-modal
      id="approve-withdrawal-modal"
      centered
      hide-footer
      header-bg-variant="success"
      header-text-variant="light"
    >
      <template #modal-title>
        <span class="text-white">Approve Withdrawal</span>
      </template>
      <div v-if="selectedItem">
        <p class="text-body mb-1">
          Approve withdrawal of
          <strong>
            {{ selectedItem.amount }} {{ selectedItem.tokenSymbol }}
          </strong>
          to
          <code>{{ maskAddress(selectedItem.address) }}</code>
          for user <strong>{{ selectedItem.username || selectedItem.userId }}</strong
          >?
        </p>
        <b-form-group label="Review Comment (optional)" label-for="approveNotes">
          <b-form-textarea
            id="approveNotes"
            v-model="approveNotes"
            placeholder="Saved as reviewComment"
            rows="3"
            max-rows="5"
          />
        </b-form-group>
        <div class="d-flex justify-content-end mt-2" style="gap: 8px">
          <b-button variant="outline-secondary" @click="closeApproveModal">
            Cancel
          </b-button>
          <b-button
            variant="success"
            :disabled="loadingUpdate"
            @click="submitApprove"
          >
            Confirm Approve
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Reject Modal -->
    <b-modal
      id="reject-withdrawal-modal"
      centered
      hide-footer
      header-bg-variant="danger"
      header-text-variant="light"
    >
      <template #modal-title>
        <span class="text-white">Reject Withdrawal</span>
      </template>
      <div v-if="selectedItem">
        <p class="text-body mb-1">
          Reject withdrawal of
          <strong>
            {{ selectedItem.amount }} {{ selectedItem.tokenSymbol }}
          </strong>
          for user
          <strong>{{ selectedItem.username || selectedItem.userId }}</strong
          >? This refunds the amount back to the user's wallet.
        </p>
        <b-form-group label="Rejection Notes (optional)" label-for="rejectNotes">
          <b-form-textarea
            id="rejectNotes"
            v-model="rejectNotes"
            placeholder="Admin rejected"
            rows="3"
            max-rows="5"
          />
        </b-form-group>
        <div class="d-flex justify-content-end mt-2" style="gap: 8px">
          <b-button variant="outline-secondary" @click="closeRejectModal">
            Cancel
          </b-button>
          <b-button
            variant="danger"
            :disabled="loadingUpdate"
            @click="submitReject"
          >
            Confirm Reject
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- View Modal -->
    <b-modal
      id="view-withdrawal-modal"
      centered
      hide-footer
      size="lg"
      header-bg-variant="dark"
      header-text-variant="light"
    >
      <template #modal-title>
        <span class="text-white">Withdrawal Details</span>
      </template>
      <div v-if="viewItem" class="text-black">
        <div class="d-flex align-items-center justify-content-between mb-1">
          <div>
            <span class="text-muted" style="font-size: 12px">Transaction ID</span>
            <p class="font-weight-bold mb-0">{{ viewItem.transactionId || "-" }}</p>
          </div>
          <b-badge
            :variant="statusVariant(viewItem.status)"
            style="font-size: 13px; padding: 6px 12px"
          >
            {{ statusLabel(viewItem.status) }}
          </b-badge>
        </div>

        <div
          style="
            border: 1px solid #0000001a;
            border-radius: 6px;
            overflow: hidden;
          "
        >
          <div
            class="px-1 py-1"
            style="
              background: #f8f9fa;
              border-bottom: 1px solid #0000001a;
            "
          >
            <span class="font-weight-bold text-body" style="font-size: 12px"
              >REQUESTER</span
            >
          </div>
          <b-row class="px-1">
            <b-col cols="6" md="4" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">User ID</p>
              <p class="font-weight-bold mb-0">{{ viewItem.userId || "-" }}</p>
            </b-col>
            <b-col cols="6" md="4" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">Username</p>
              <p class="font-weight-bold mb-0">{{ viewItem.username || "-" }}</p>
            </b-col>
            <b-col cols="6" md="4" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">Full Name</p>
              <p class="font-weight-bold mb-0">
                {{
                  [viewItem.firstName, viewItem.middleName, viewItem.lastName]
                    .filter(Boolean)
                    .join(" ") || "-"
                }}
              </p>
            </b-col>
          </b-row>

          <div
            class="px-1 py-1"
            style="
              background: #f8f9fa;
              border-top: 1px solid #0000001a;
              border-bottom: 1px solid #0000001a;
            "
          >
            <span class="font-weight-bold text-body" style="font-size: 12px"
              >CRYPTO</span
            >
          </div>
          <b-row class="px-1">
            <b-col cols="6" md="4" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">Token</p>
              <p class="font-weight-bold mb-0">
                {{ viewItem.tokenSymbol || "-" }}
                <small v-if="viewItem.network" class="text-muted">
                  ({{ viewItem.network }})
                </small>
              </p>
            </b-col>
            <b-col cols="6" md="4" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">Amount</p>
              <p class="font-weight-bold mb-0 text-primary">
                {{ viewItem.amount || "-" }}
              </p>
            </b-col>
            <b-col cols="6" md="4" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">
                Destination Tag
              </p>
              <p class="font-weight-bold mb-0">
                {{ viewItem.destinationTag || "-" }}
              </p>
            </b-col>
            <b-col cols="12" md="8" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">Address</p>
              <div class="d-flex align-items-center" style="gap: 6px">
                <p class="font-weight-bold mb-0" style="word-break: break-all">
                  {{ viewItem.address || "-" }}
                </p>
                <feather-icon
                  v-if="viewItem.address"
                  icon="CopyIcon"
                  size="14"
                  class="cursor-pointer text-primary"
                  @click="copyToClipboard(viewItem.address, 'Address copied')"
                />
              </div>
            </b-col>
            <b-col cols="12" md="4" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">Payment ID</p>
              <div class="d-flex align-items-center" style="gap: 6px">
                <p class="font-weight-bold mb-0" style="word-break: break-all">
                  {{ viewItem.paymentId || "-" }}
                </p>
                <feather-icon
                  v-if="viewItem.paymentId"
                  icon="CopyIcon"
                  size="14"
                  class="cursor-pointer text-primary"
                  @click="
                    copyToClipboard(viewItem.paymentId, 'Payment ID copied')
                  "
                />
              </div>
            </b-col>
          </b-row>

          <div
            class="px-1 py-1"
            style="
              background: #f8f9fa;
              border-top: 1px solid #0000001a;
              border-bottom: 1px solid #0000001a;
            "
          >
            <span class="font-weight-bold text-body" style="font-size: 12px"
              >TIMELINE</span
            >
          </div>
          <b-row class="px-1">
            <b-col cols="6" md="4" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">Requested</p>
              <p class="font-weight-bold mb-0">
                {{
                  viewItem.createdTime
                    ? format(
                        new Date(viewItem.createdTime),
                        "dd-MM-yyyy HH:mm"
                      )
                    : "-"
                }}
              </p>
            </b-col>
            <b-col cols="6" md="4" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">Updated</p>
              <p class="font-weight-bold mb-0">
                {{
                  viewItem.updatedTime
                    ? format(
                        new Date(viewItem.updatedTime),
                        "dd-MM-yyyy HH:mm"
                      )
                    : "-"
                }}
              </p>
            </b-col>
            <b-col cols="6" md="4" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">Completed</p>
              <p class="font-weight-bold mb-0">
                {{
                  viewItem.completedTime
                    ? format(
                        new Date(viewItem.completedTime),
                        "dd-MM-yyyy HH:mm"
                      )
                    : "-"
                }}
              </p>
            </b-col>
          </b-row>

          <div
            v-if="
              viewItem.reviewComment || viewItem.notes || viewItem.error
            "
            class="px-1 py-1"
            style="
              background: #f8f9fa;
              border-top: 1px solid #0000001a;
              border-bottom: 1px solid #0000001a;
            "
          >
            <span class="font-weight-bold text-body" style="font-size: 12px"
              >NOTES</span
            >
          </div>
          <b-row class="px-1">
            <b-col v-if="viewItem.reviewComment" cols="12" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">
                Review Comment
              </p>
              <p class="mb-0">{{ viewItem.reviewComment }}</p>
            </b-col>
            <b-col v-if="viewItem.notes" cols="12" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">Notes</p>
              <p class="mb-0">{{ viewItem.notes }}</p>
            </b-col>
            <b-col v-if="viewItem.error" cols="12" class="py-1">
              <p class="text-muted mb-0" style="font-size: 11px">Error</p>
              <p class="mb-0 text-danger">{{ viewItem.error }}</p>
            </b-col>
          </b-row>

          <template v-if="viewItem.meta && Object.keys(viewItem.meta).length">
            <div
              class="px-1 py-1"
              style="
                background: #f8f9fa;
                border-top: 1px solid #0000001a;
                border-bottom: 1px solid #0000001a;
              "
            >
              <span class="font-weight-bold text-body" style="font-size: 12px"
                >META</span
              >
            </div>
            <b-row class="px-1">
              <b-col cols="12" class="py-1">
                <pre class="mb-0" style="white-space: pre-wrap">{{
                  prettyJson(viewItem.meta)
                }}</pre>
              </b-col>
            </b-row>
          </template>
        </div>

        <div class="d-flex justify-content-end mt-2" style="gap: 8px">
          <b-button
            variant="outline-secondary"
            @click="$bvModal.hide('view-withdrawal-modal')"
          >
            Close
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BFormGroup,
  BFormSelect,
  BFormInput,
  BButton,
  BModal,
  BTable,
  BBadge,
  BButtonGroup,
  BPagination,
  BFormTextarea,
} from "bootstrap-vue";
import { mapState, mapActions } from "vuex";
import flatPickr from "vue-flatpickr-component";
import { format, subDays, startOfToday } from "date-fns";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";

const WDL_STATUSES = [
  "wdl_new",
  "wdl_new_error",
  "wdl_review",
  "wdl_pending",
  "wdl_pending_provider",
  "wdl_pending_synced",
  "wdl_executed",
  "wdl_failed",
  "wdl_canceled",
];

const STATUS_VARIANT_MAP = {
  wdl_new: "light-info",
  wdl_new_error: "light-danger",
  wdl_review: "light-warning",
  wdl_pending: "light-primary",
  wdl_pending_provider: "light-primary",
  wdl_pending_synced: "light-primary",
  wdl_executed: "light-success",
  wdl_failed: "light-danger",
  wdl_canceled: "light-secondary",
};

const STATUS_LABEL_MAP = {
  wdl_new: "New",
  wdl_new_error: "New Error",
  wdl_review: "Review",
  wdl_pending: "Pending",
  wdl_pending_provider: "Pending Provider",
  wdl_pending_synced: "Pending Synced",
  wdl_executed: "Executed",
  wdl_failed: "Failed",
  wdl_canceled: "Canceled",
};

const CANCELABLE_STATUSES = new Set([
  "wdl_new",
  "wdl_review",
  "wdl_pending",
]);

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BButton,
    BModal,
    BTable,
    BBadge,
    BButtonGroup,
    BPagination,
    BFormTextarea,
    flatPickr,
  },
  data() {
    return {
      dateRanges: [
        { value: "custom", text: "Custom" },
        { value: "today", text: "Today" },
        { value: "7days", text: "Last 7 Days" },
        { value: "30days", text: "Last 30 Days" },
        { value: "90days", text: "Last 90 Days" },
      ],
      statusOptions: [
        { value: "all", text: "All" },
        ...WDL_STATUSES.map((value) => ({
          value,
          text: STATUS_LABEL_MAP[value],
        })),
      ],
      filters: {
        dateRange: "custom",
        startDate: "",
        endDate: "",
        status: "all",
        tokenSymbol: "",
        userId: "",
      },
      fields: [
        { key: "transactionId", label: "Txn ID", thStyle: { minWidth: "120px" } },
        { key: "requester", label: "Requester", thStyle: { minWidth: "140px" } },
        { key: "userId", label: "User ID", thStyle: { minWidth: "100px" } },
        { key: "token", label: "Token", thStyle: { minWidth: "110px" } },
        { key: "amount", label: "Amount", thStyle: { minWidth: "100px" } },
        { key: "tokenSymbol", label: "Currency Type" },
        { key: "address", label: "Address", thStyle: { minWidth: "160px" } },
        {
          key: "destinationTag",
          label: "Dest Tag",
          thStyle: { minWidth: "100px" },
        },
        { key: "paymentId", label: "Payment ID", thStyle: { minWidth: "130px" } },
        { key: "createdTime", label: "Requested", thStyle: { minWidth: "130px" } },
        {
          key: "completedTime",
          label: "Completed",
          thStyle: { minWidth: "130px" },
        },
        { key: "status", label: "Status", thStyle: { minWidth: "120px" } },
        { key: "notes", label: "Notes", thStyle: { minWidth: "180px" } },
        { key: "actions", label: "Actions", thStyle: { minWidth: "180px" } },
      ],
      page: 1,
      limit: 25,
      limitOptions: [10, 25, 50, 100],
      configFrom: {
        altFormat: "d-m-Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      configTo: {
        altFormat: "d-m-Y",
        altInput: true,
        dateFormat: "Y-m-d",
        minDate: null,
      },
      selectedItem: null,
      viewItem: null,
      approveNotes: "",
      rejectNotes: "",
    };
  },
  computed: {
    ...mapState("finance/withdrawal", {
      items: "data",
      pagination: "pagination",
      loading: "loading",
      loadingUpdate: "loadingUpdate",
    }),
    paginationSummary() {
      const p = this.pagination || {};
      const page = Number(p.page) || 1;
      const limit = Number(p.limit || p.perPage) || 10;
      const total = Number(p.total) || 0;
      const from = total === 0 ? 0 : (page - 1) * limit + 1;
      const to = total === 0 ? 0 : Math.min(page * limit, total);
      return total === 0
        ? "Showing 0 of 0 entries"
        : `Showing ${from} - ${to} of ${total} entries`;
    },
  },
  watch: {
    page() {
      this.fetchWithdrawal();
    },
    limit() {
      this.page = 1;
      this.fetchWithdrawal();
    },
    "filters.startDate"(newVal) {
      this.configTo = { ...this.configTo, minDate: newVal || null };
      this.resetPageAndFetch();
    },
    "filters.endDate"(newVal) {
      this.configFrom = { ...this.configFrom, maxDate: newVal || null };
      this.resetPageAndFetch();
    },
    "filters.status"() {
      this.resetPageAndFetch();
    },
    "filters.tokenSymbol"() {
      this.resetPageAndFetch();
    },
    "filters.userId"() {
      this.resetPageAndFetch();
    },
    "filters.dateRange"(val) {
      const fmt = (date) => format(date, "yyyy-MM-dd");
      if (val === "custom") {
        this.filters.startDate = "";
        this.filters.endDate = "";
        return;
      }
      const endDate = startOfToday();
      const daysMap = { today: 0, "7days": 7, "30days": 30, "90days": 90 };
      const days = daysMap[val];
      if (days !== undefined) {
        const startDate = subDays(endDate, days);
        this.filters.startDate = fmt(startDate);
        this.filters.endDate = fmt(endDate);
      }
    },
  },
  created() {
    this.fetchWithdrawal();
  },
  methods: {
    ...mapActions("finance/withdrawal", ["getWithdrawal", "updateWithdrawal"]),
    format,
    formatCurrencyBuiltInFunc,
    resetPageAndFetch() {
      if (this.page !== 1) {
        this.page = 1;
        return;
      }
      this.fetchWithdrawal();
    },
    async fetchWithdrawal() {
      if (this.loading) return;
      const params = {
        page: this.page,
        limit: this.limit,
        withUserUsage: 1,
      };
      if (this.filters.status && this.filters.status !== "all") {
        params.status = this.filters.status;
      }
      if (this.filters.tokenSymbol) {
        params.tokenSymbol = this.filters.tokenSymbol.trim().toUpperCase();
      }
      if (this.filters.userId) {
        params.userId = this.filters.userId.trim();
      }
      if (this.filters.startDate) {
        params.createdFrom = new Date(
          `${this.filters.startDate}T00:00:00.000Z`,
        ).toISOString();
      }
      if (this.filters.endDate) {
        params.createdTo = new Date(
          `${this.filters.endDate}T23:59:59.999Z`,
        ).toISOString();
      }
      await this.getWithdrawal(params);
    },
    handleClearFilters() {
      this.filters = {
        dateRange: "custom",
        startDate: "",
        endDate: "",
        status: "all",
        tokenSymbol: "",
        userId: "",
      };
    },
    canApprove(status) {
      return status === "wdl_review";
    },
    canReject(status) {
      return CANCELABLE_STATUSES.has(status);
    },
    statusVariant(status) {
      return STATUS_VARIANT_MAP[status] || "light-secondary";
    },
    statusLabel(status) {
      return STATUS_LABEL_MAP[status] || status || "-";
    },
    openApproveModal(item) {
      this.selectedItem = item;
      this.approveNotes = "";
      this.$bvModal.show("approve-withdrawal-modal");
    },
    closeApproveModal() {
      this.$bvModal.hide("approve-withdrawal-modal");
      this.selectedItem = null;
      this.approveNotes = "";
    },
    async submitApprove() {
      if (!this.selectedItem || this.loadingUpdate) return;
      const res = await this.updateWithdrawal({
        id: this.selectedItem.withdrawalRequestId,
        status: "wdl_pending",
        notes: this.approveNotes || undefined,
      });
      if (res) {
        this.closeApproveModal();
      }
    },
    openRejectModal(item) {
      this.selectedItem = item;
      this.rejectNotes = "";
      this.$bvModal.show("reject-withdrawal-modal");
    },
    closeRejectModal() {
      this.$bvModal.hide("reject-withdrawal-modal");
      this.selectedItem = null;
      this.rejectNotes = "";
    },
    async submitReject() {
      if (!this.selectedItem || this.loadingUpdate) return;
      const res = await this.updateWithdrawal({
        id: this.selectedItem.withdrawalRequestId,
        status: "wdl_canceled",
        notes: this.rejectNotes || undefined,
      });
      if (res) {
        this.closeRejectModal();
      }
    },
    openViewModal(item) {
      this.viewItem = item;
      this.$bvModal.show("view-withdrawal-modal");
    },
    maskAddress(address) {
      if (!address) return "-";
      if (address.length <= 12) return address;
      return `${address.slice(0, 6)}...${address.slice(-4)}`;
    },
    truncateMiddle(str, keep = 6) {
      if (!str) return "-";
      if (str.length <= keep * 2) return str;
      return `${str.slice(0, keep)}...${str.slice(-4)}`;
    },
    async copyToClipboard(value, successMessage) {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        this.$root.$bvToast.toast(successMessage || "Copied", {
          title: "Copied",
          variant: "success",
          solid: true,
          autoHideDelay: 1500,
        });
      } catch (e) {
        this.$root.$bvToast.toast("Copy failed", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },
    prettyJson(obj) {
      try {
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        return String(obj);
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
.withdrawal-table th,
.withdrawal-table td {
  padding: 8px 5px !important;
}
</style>
